import React, { useState } from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Counterparty {
  id: string;
  name: string;
  type: 'Bank' | 'Fund' | 'Corporate';
  exposure: number;
  limit: number;
  netExposure: number;
  assetClasses: string[];
  rating: string;
  jurisdiction: string;
  nettingAgreement: boolean;
  lastUpdated: string;
  assetBreakdown: { class: string; amount: number }[];
  csaTerms: string;
  nettingSets: number;
  mta: number;
  threshold: number;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const COUNTERPARTIES: Counterparty[] = [
  {
    id: 'cp1',
    name: 'Goldman Sachs',
    type: 'Bank',
    exposure: 450,
    limit: 500,
    netExposure: 312,
    assetClasses: ['IRS', 'CDS', 'FX Options'],
    rating: 'A+',
    jurisdiction: 'US',
    nettingAgreement: true,
    lastUpdated: '2026-02-20 09:15',
    assetBreakdown: [
      { class: 'IRS', amount: 210 },
      { class: 'CDS', amount: 140 },
      { class: 'FX Options', amount: 100 },
    ],
    csaTerms: 'ISDA 2002 Master + NY Law CSA',
    nettingSets: 3,
    mta: 0.5,
    threshold: 0,
  },
  {
    id: 'cp2',
    name: 'JPMorgan',
    type: 'Bank',
    exposure: 320,
    limit: 400,
    netExposure: 218,
    assetClasses: ['IRS', 'FX Options'],
    rating: 'AA-',
    jurisdiction: 'US',
    nettingAgreement: true,
    lastUpdated: '2026-02-20 09:10',
    assetBreakdown: [
      { class: 'IRS', amount: 195 },
      { class: 'FX Options', amount: 125 },
    ],
    csaTerms: 'ISDA 2002 Master + English Law CSA',
    nettingSets: 2,
    mta: 1.0,
    threshold: 5,
  },
  {
    id: 'cp3',
    name: 'Deutsche Bank',
    type: 'Bank',
    exposure: 280,
    limit: 300,
    netExposure: 261,
    assetClasses: ['IRS', 'CDS'],
    rating: 'BBB+',
    jurisdiction: 'UK',
    nettingAgreement: true,
    lastUpdated: '2026-02-20 09:05',
    assetBreakdown: [
      { class: 'IRS', amount: 165 },
      { class: 'CDS', amount: 115 },
    ],
    csaTerms: 'ISDA 2002 Master + German Law CSA',
    nettingSets: 2,
    mta: 0.5,
    threshold: 0,
  },
  {
    id: 'cp4',
    name: 'HSBC HK',
    type: 'Bank',
    exposure: 180,
    limit: 300,
    netExposure: 142,
    assetClasses: ['FX Options', 'IRS'],
    rating: 'AA-',
    jurisdiction: 'HK',
    nettingAgreement: true,
    lastUpdated: '2026-02-20 09:00',
    assetBreakdown: [
      { class: 'FX Options', amount: 105 },
      { class: 'IRS', amount: 75 },
    ],
    csaTerms: 'ISDA 2002 Master + HK Law CSA',
    nettingSets: 1,
    mta: 1.0,
    threshold: 10,
  },
  {
    id: 'cp5',
    name: 'Citadel',
    type: 'Fund',
    exposure: 95,
    limit: 100,
    netExposure: 88,
    assetClasses: ['CDS', 'FX Options'],
    rating: 'NR',
    jurisdiction: 'US',
    nettingAgreement: true,
    lastUpdated: '2026-02-20 08:55',
    assetBreakdown: [
      { class: 'CDS', amount: 60 },
      { class: 'FX Options', amount: 35 },
    ],
    csaTerms: 'ISDA 2002 Master + Prime Brokerage CSA',
    nettingSets: 1,
    mta: 0.25,
    threshold: 0,
  },
  {
    id: 'cp6',
    name: 'BlackRock',
    type: 'Fund',
    exposure: 140,
    limit: 200,
    netExposure: 118,
    assetClasses: ['IRS', 'CDS'],
    rating: 'NR',
    jurisdiction: 'US',
    nettingAgreement: true,
    lastUpdated: '2026-02-20 08:50',
    assetBreakdown: [
      { class: 'IRS', amount: 90 },
      { class: 'CDS', amount: 50 },
    ],
    csaTerms: 'ISDA 2002 Master + Standard CSA',
    nettingSets: 2,
    mta: 0.5,
    threshold: 5,
  },
  {
    id: 'cp7',
    name: 'Bank of China',
    type: 'Bank',
    exposure: 210,
    limit: 250,
    netExposure: 185,
    assetClasses: ['IRS', 'FX Options'],
    rating: 'A',
    jurisdiction: 'HK',
    nettingAgreement: true,
    lastUpdated: '2026-02-20 08:45',
    assetBreakdown: [
      { class: 'IRS', amount: 125 },
      { class: 'FX Options', amount: 85 },
    ],
    csaTerms: 'ISDA 2002 Master + HK Law CSA',
    nettingSets: 2,
    mta: 1.0,
    threshold: 0,
  },
  {
    id: 'cp8',
    name: 'Macquarie',
    type: 'Bank',
    exposure: 85,
    limit: 150,
    netExposure: 71,
    assetClasses: ['IRS', 'CDS', 'FX Options'],
    rating: 'A-',
    jurisdiction: 'SG',
    nettingAgreement: false,
    lastUpdated: '2026-02-20 08:40',
    assetBreakdown: [
      { class: 'IRS', amount: 42 },
      { class: 'CDS', amount: 28 },
      { class: 'FX Options', amount: 15 },
    ],
    csaTerms: 'ISDA 2002 Master (no CSA)',
    nettingSets: 0,
    mta: 0,
    threshold: 0,
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function utilization(exp: number, limit: number): number {
  return Math.round((exp / limit) * 100);
}

function statusInfo(exp: number, limit: number): { label: string; color: string; bg: string } {
  const u = utilization(exp, limit);
  if (u >= 90) return { label: 'BREACH', color: 'text-red-700', bg: 'bg-red-100' };
  if (u >= 75) return { label: 'WATCH', color: 'text-amber-700', bg: 'bg-amber-100' };
  return { label: 'OK', color: 'text-green-700', bg: 'bg-green-100' };
}

function ratingColor(rating: string): string {
  if (rating.startsWith('AA')) return 'text-emerald-700 bg-emerald-50';
  if (rating.startsWith('A')) return 'text-blue-700 bg-blue-50';
  if (rating.startsWith('BBB')) return 'text-amber-700 bg-amber-50';
  return 'text-slate-600 bg-slate-100';
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function CounterpartyMonitorView() {
  const [typeFilter, setTypeFilter] = useState<string>('All');
  const [jurisFilter, setJurisFilter] = useState<string>('All');
  const [selected, setSelected] = useState<Counterparty | null>(COUNTERPARTIES[2]);

  const totalExposure = COUNTERPARTIES.reduce((s, c) => s + c.exposure, 0);
  const breaches = COUNTERPARTIES.filter(c => utilization(c.exposure, c.limit) >= 90).length;
  const avgUtil = Math.round(
    COUNTERPARTIES.reduce((s, c) => s + utilization(c.exposure, c.limit), 0) / COUNTERPARTIES.length
  );

  const jurisdictions = ['All', ...Array.from(new Set(COUNTERPARTIES.map(c => c.jurisdiction)))];

  const filtered = COUNTERPARTIES.filter(c => {
    const typeOk = typeFilter === 'All' || c.type === typeFilter || (typeFilter === 'Banks' && c.type === 'Bank') || (typeFilter === 'Funds' && c.type === 'Fund');
    const jurisOk = jurisFilter === 'All' || c.jurisdiction === jurisFilter;
    return typeOk && jurisOk;
  });

  return (
    <div className="flex flex-col h-full bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-rose-100 flex items-center justify-center">
            <svg className="w-4 h-4 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Counterparty Credit Monitor</h1>
            <p className="text-xs text-slate-500">OTC Derivatives Exposure vs Credit Limits — Type 11 SFC</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-slate-400">Last refresh:</span>
            <span className="text-xs font-medium text-slate-600">2026-02-20 09:15 HKT</span>
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6 space-y-5">
        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-4">
          <SummaryCard
            label="Total Gross Exposure"
            value={`$${totalExposure.toLocaleString()}M`}
            sub="USD millions"
            accent="rose"
          />
          <SummaryCard
            label="Active Counterparties"
            value={String(COUNTERPARTIES.length)}
            sub="OTC relationships"
            accent="blue"
          />
          <SummaryCard
            label="Limit Breaches"
            value={String(breaches)}
            sub="Require immediate action"
            accent={breaches > 0 ? 'red' : 'green'}
            highlight={breaches > 0}
          />
          <SummaryCard
            label="Avg Limit Utilization"
            value={`${avgUtil}%`}
            sub="Across all counterparties"
            accent="amber"
          />
        </div>

        {/* Filter Bar */}
        <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-4 py-2.5">
          <span className="text-xs font-medium text-slate-500 mr-1">Type:</span>
          {['All', 'Banks', 'Funds'].map(f => (
            <button
              key={f}
              onClick={() => setTypeFilter(f)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                typeFilter === f
                  ? 'bg-rose-100 text-rose-700'
                  : 'text-slate-500 hover:bg-slate-100'
              }`}
            >
              {f}
            </button>
          ))}
          <div className="w-px h-4 bg-slate-200 mx-2" />
          <span className="text-xs font-medium text-slate-500">Jurisdiction:</span>
          {jurisdictions.map(j => (
            <button
              key={j}
              onClick={() => setJurisFilter(j)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                jurisFilter === j
                  ? 'bg-rose-100 text-rose-700'
                  : 'text-slate-500 hover:bg-slate-100'
              }`}
            >
              {j}
            </button>
          ))}
          <span className="ml-auto text-xs text-slate-400">{filtered.length} counterparties</span>
        </div>

        {/* Main Layout */}
        <div className="flex gap-5">
          {/* Table */}
          <div className="flex-1 bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Counterparty</th>
                  <th className="text-left px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Rtg</th>
                  <th className="text-left px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Type</th>
                  <th className="text-left px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Asset Classes</th>
                  <th className="text-right px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Gross $M</th>
                  <th className="text-right px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Net $M</th>
                  <th className="text-right px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Limit $M</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide w-32">Utilization</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(cp => {
                  const u = utilization(cp.exposure, cp.limit);
                  const st = statusInfo(cp.exposure, cp.limit);
                  const isSelected = selected?.id === cp.id;
                  return (
                    <tr
                      key={cp.id}
                      onClick={() => setSelected(cp)}
                      className={`border-b border-slate-100 cursor-pointer transition-colors ${
                        isSelected ? 'bg-rose-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900 text-sm">{cp.name}</div>
                        <div className="text-xs text-slate-400">{cp.jurisdiction} · {cp.nettingAgreement ? 'Netting ✓' : 'No netting'}</div>
                      </td>
                      <td className="px-3 py-3">
                        <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${ratingColor(cp.rating)}`}>
                          {cp.rating}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-xs text-slate-600">{cp.type}</td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-1">
                          {cp.assetClasses.map(ac => (
                            <span key={ac} className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                              {ac}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right font-mono text-sm font-medium text-slate-900">
                        {cp.exposure.toLocaleString()}
                      </td>
                      <td className="px-3 py-3 text-right font-mono text-sm text-slate-600">
                        {cp.netExposure.toLocaleString()}
                      </td>
                      <td className="px-3 py-3 text-right font-mono text-sm text-slate-500">
                        {cp.limit.toLocaleString()}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${
                                u >= 90 ? 'bg-red-500' : u >= 75 ? 'bg-amber-400' : 'bg-emerald-500'
                              }`}
                              style={{ width: `${Math.min(u, 100)}%` }}
                            />
                          </div>
                          <span className={`text-xs font-semibold w-8 text-right ${
                            u >= 90 ? 'text-red-600' : u >= 75 ? 'text-amber-600' : 'text-slate-600'
                          }`}>
                            {u}%
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={`text-xs font-bold px-2 py-1 rounded-md ${st.bg} ${st.color}`}>
                          {st.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Detail Panel */}
          {selected && (
            <div className="w-80 bg-white border border-slate-200 rounded-lg overflow-hidden flex flex-col">
              <div className="bg-rose-600 px-4 py-3">
                <div className="font-semibold text-white">{selected.name}</div>
                <div className="text-rose-200 text-xs mt-0.5">{selected.type} · {selected.jurisdiction} · {selected.rating}</div>
              </div>
              <div className="flex-1 overflow-auto p-4 space-y-4">
                {/* Utilization */}
                <div>
                  <div className="flex justify-between text-xs text-slate-500 mb-1">
                    <span>Limit Utilization</span>
                    <span className="font-semibold">{utilization(selected.exposure, selected.limit)}%</span>
                  </div>
                  <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${
                        utilization(selected.exposure, selected.limit) >= 90
                          ? 'bg-red-500'
                          : utilization(selected.exposure, selected.limit) >= 75
                          ? 'bg-amber-400'
                          : 'bg-emerald-500'
                      }`}
                      style={{ width: `${Math.min(utilization(selected.exposure, selected.limit), 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>$0</span>
                    <span>Limit: ${selected.limit}M</span>
                  </div>
                </div>

                {/* Exposure Breakdown */}
                <div>
                  <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Exposure by Asset Class</div>
                  <div className="space-y-2">
                    {selected.assetBreakdown.map(ab => (
                      <div key={ab.class}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-slate-600">{ab.class}</span>
                          <span className="font-medium text-slate-800">${ab.amount}M</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-rose-400 rounded-full"
                            style={{ width: `${(ab.amount / selected.exposure) * 100}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CSA Terms */}
                <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                  <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">CSA / Netting Terms</div>
                  <div className="text-xs text-slate-600">{selected.csaTerms}</div>
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <div>
                      <div className="text-xs text-slate-400">Netting Sets</div>
                      <div className="text-sm font-semibold text-slate-800">{selected.nettingSets}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-400">MTA</div>
                      <div className="text-sm font-semibold text-slate-800">${selected.mta}M</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-400">Threshold</div>
                      <div className="text-sm font-semibold text-slate-800">${selected.threshold}M</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-400">Netting</div>
                      <div className={`text-sm font-semibold ${selected.nettingAgreement ? 'text-emerald-700' : 'text-red-600'}`}>
                        {selected.nettingAgreement ? 'Active' : 'None'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-2 gap-2">
                  <MetricBox label="Gross Exposure" value={`$${selected.exposure}M`} />
                  <MetricBox label="Net Exposure" value={`$${selected.netExposure}M`} />
                  <MetricBox label="Credit Limit" value={`$${selected.limit}M`} />
                  <MetricBox label="Headroom" value={`$${selected.limit - selected.exposure}M`} />
                </div>

                <div className="text-xs text-slate-400 text-center">
                  Updated: {selected.lastUpdated} HKT
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SummaryCard({
  label, value, sub, accent, highlight,
}: {
  label: string; value: string; sub: string; accent: string; highlight?: boolean;
}) {
  const accentMap: Record<string, string> = {
    rose: 'border-rose-300 bg-rose-50',
    blue: 'border-blue-200 bg-blue-50',
    red: 'border-red-300 bg-red-50',
    green: 'border-green-200 bg-green-50',
    amber: 'border-amber-200 bg-amber-50',
  };
  const valMap: Record<string, string> = {
    rose: 'text-rose-700',
    blue: 'text-blue-700',
    red: 'text-red-700',
    green: 'text-emerald-700',
    amber: 'text-amber-700',
  };
  return (
    <div className={`bg-white border rounded-lg p-4 ${highlight ? 'border-red-400 ring-1 ring-red-200' : 'border-slate-200'}`}>
      <div className="text-xs text-slate-500 mb-1">{label}</div>
      <div className={`text-2xl font-bold ${valMap[accent] ?? 'text-slate-800'}`}>{value}</div>
      <div className="text-xs text-slate-400 mt-1">{sub}</div>
    </div>
  );
}

function MetricBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-slate-50 rounded-md p-2.5">
      <div className="text-xs text-slate-400">{label}</div>
      <div className="text-sm font-semibold text-slate-800 mt-0.5">{value}</div>
    </div>
  );
}